import type { components } from '@trackercop/contracts';

type DriftReport = components['schemas']['DriftReport'];
type PayloadCategory = components['schemas']['PayloadCategory'];

// Mirror of BaselineComparator's severity ladder (packages/common).
// The OpenAPI DriftReport does not carry `driftSeverity`, so we compute
// it client-side using the same rule the server comparator uses:
//
//   - major: any added payload category is pii / identifier /
//            device-fingerprint, OR any drifted-script signals PII
//   - minor: any added payload category is advertising / behavioral,
//            OR there are drifted scripts / new destinations
//   - none:  every added category is benign/product and no drift signals
//
// If the server ever grows a `driftSeverity` field, swap this out for
// the server-provided value — computing locally is pragmatic, not
// authoritative.
const MAJOR_CATEGORIES: ReadonlySet<PayloadCategory> = new Set<PayloadCategory>([
  'pii',
  'identifier',
  'device-fingerprint',
]);

const MINOR_CATEGORIES: ReadonlySet<PayloadCategory> = new Set<PayloadCategory>([
  'advertising',
  'behavioral',
]);

export type DriftSeverity = 'none' | 'minor' | 'major';

export function computeDriftSeverity(report: DriftReport): DriftSeverity {
  const newCategories = report.newPayloadCategories ?? [];
  const drifted = report.driftedScripts ?? [];
  const newDests = report.newDestinations ?? [];
  const newScripts = report.newScripts ?? [];

  for (const c of newCategories) {
    if (MAJOR_CATEGORIES.has(c)) return 'major';
  }
  for (const d of drifted) {
    const changes = d.changes ?? [];
    for (const change of changes) {
      const lower = change.toLowerCase();
      if (lower.includes('pii') || lower.includes('identifier') || lower.includes('fingerprint')) {
        return 'major';
      }
    }
  }
  for (const c of newCategories) {
    if (MINOR_CATEGORIES.has(c)) return 'minor';
  }
  if (drifted.length > 0) return 'minor';
  if (newDests.length > 0) return 'minor';
  if (newScripts.length > 0) return 'minor';
  return 'none';
}

export function hasAnyDrift(report: DriftReport): boolean {
  return (
    (report.newScripts?.length ?? 0) > 0 ||
    (report.removedScripts?.length ?? 0) > 0 ||
    (report.driftedScripts?.length ?? 0) > 0 ||
    (report.newDestinations?.length ?? 0) > 0 ||
    (report.newPayloadCategories?.length ?? 0) > 0 ||
    (report.newGTMTags?.length ?? 0) > 0
  );
}
